import { DAEMON_URL } from "../config";

export type CodeReviewState = string;
export type CodeReviewSource = "automatic" | "manual";

/** One item in the pr-reviewer queue for a repo. Mirrors the pr-reviewer
 *  QueueItem shape (the daemon proxies it through /projects/:project/code-review).
 *  Fields are snake_case to match the upstream service. `cost_usd` is admin-only:
 *  the daemon strips it (sets it to null) for non-admin callers. */
export interface CodeReviewItem {
  id: number;
  repo: string;
  pr_number: number;
  pr_title: string;
  head_branch: string;
  head_sha: string;
  state: CodeReviewState;
  source: CodeReviewSource;
  fix_rounds: number;
  qa_cycles: number;
  /** Full-pipeline attempts so far (0 on the first attempt). */
  attempts?: number;
  cost_usd?: number | null;
  error: string | null;
  /** 1-based position in the queue (manual items first). */
  position: number;
  created_at: string;
  updated_at: string;
  /** When the current stage was entered (server clock). Optional: older
   *  pr-reviewer builds omit it, so callers fall back to updated_at. */
  stage_since?: string;
  /** Stage-transition timeline for this run, oldest first. Optional for the
   *  same backward-compat reason. */
  steps?: Array<{ state: string; at: string }>;
}

export interface CodeReviewQueueResponse {
  items: CodeReviewItem[];
  count: number;
  /** False when the daemon has no pr-reviewer URL/token configured. */
  configured: boolean;
  repo?: string;
  /** True when the pr-reviewer's background worker is processing the queue. */
  worker_enabled?: boolean;
  /** Present when the pr-reviewer was unreachable / errored (degraded, not fatal). */
  error?: string;
  upstream?: number;
  note?: string;
}

/** How serious a ledger finding is, most serious first. */
export type LedgerSeverity = "BLOCK" | "FLAG" | "SUGGEST";

/** One finding in the pr-reviewer's ledger for a repo. Mirrors the pr-reviewer
 *  LedgerRow (the daemon proxies it through /projects/:project/code-review/ledger).
 *  Fields are snake_case to match the upstream service. */
export interface LedgerFinding {
  id: number;
  file: string;
  line: number | null;
  severity: LedgerSeverity;
  category: string;
  message: string;
  explanation: string;
  suggestion: string | null;
  status: string;
  first_seen_sha: string;
  created_at: string;
  /** The pull request that introduced this finding. Null for a direct push,
   *  and when Bob would not guess between several. */
  pr_number: number | null;
  /** Who clicked Solved on Bob's Discord card. A claim, not a verification:
   *  `status` stays open under it, so it never renders the finding as resolved. */
  solved_by: string | null;
  /** When that claim was made. */
  solved_at: string | null;

  // Bob's row passes through unread, so a Bob that predates a column sends it
  // absent rather than null: every field from here on is optional.

  /** The most recent commit a pass saw this defect on. */
  last_seen_sha?: string | null;
  /** When `last_seen_sha` was seen. Never borrowed from `updated_at`, which
   *  moves on writes that are not sightings. */
  last_seen_at?: string | null;
  /** The commit a pass verified the defect was gone on. */
  resolved_sha?: string | null;
  /** The verifier's evidence for closing it, not a status word. */
  resolved_reason?: string | null;
  /** Who dismissed it, set only alongside `status: "dismissed"`. Absent for a member. */
  dismissed_by?: string | null;
  dismissed_reason?: string | null;
  /** How many times the defect came back after a pass closed it. */
  regressed_count?: number;
  /** Last write to the row, of any kind. It dates a fix, the only write that
   *  leaves a row fixed, but not a dismissal: a pass that sees a dismissed
   *  defect again rewrites it. */
  updated_at?: string;
  /** Set by the fix pipeline, never by Bob; nothing renders it. */
  fix_branch?: string | null;
  fix_pr?: number | null;

  /** What a player observes. The card leads with it, else with `message`. */
  player_impact?: string | null;
  /** Numbered in-game steps, newline separated, numbered by the model. */
  repro_steps?: string | null;
  /** Whether anybody ran those steps. Null means no reproduction was made. */
  repro_status?: ReproStatus | string | null;
  /** How big the repair is. Independent of severity: a BLOCK can be small. */
  effort?: LedgerEffort | string | null;
}

/** `predicted` is the only value Bob writes; `verified` and `refuted` arrive
 *  when Emil runs the steps. */
export type ReproStatus = "predicted" | "verified" | "refuted";

export type LedgerEffort = "small" | "medium" | "large";

/** The header numbers: open findings broken out, plus the two closed tallies.
 *  `open` is keyed by severity upstream but stays a plain record here, because
 *  the daemon passes the pr-reviewer's counts through without rebuilding them. */
export interface LedgerCounts {
  open: Record<string, number>;
  fixed_last_7d: number;
  dismissed: number;
}

/** The last review pass over this repo and branch. Mirrors the pr-reviewer
 *  RunRow's identity fields plus the two a caller here reads. The pr-reviewer's
 *  run carries further counters (`found`, `checked`, `dropped` and more): add
 *  one below when something in this app reads it, rather than declaring the
 *  whole set and leaving most of it unused.
 *
 *  `cost_usd` is null for non-admin members because the daemon nulls it, which
 *  is the money gate working rather than a value that went missing. `error`
 *  non-null means the pass did not complete, so its counters stopped wherever
 *  it stopped. */
export interface LedgerRun {
  id: number;
  repo: string;
  branch: string;
  /** "seed", "push" or "manual" upstream. Typed loosely because the daemon
   *  passes the value through, so a kind added later must still parse. */
  kind: string;
  base_sha: string | null;
  head_sha: string;
  created_at: string;
  cost_usd: number | null;
  error: string | null;
  /** The seed job that displaced this push. Such a row carries an `error` and
   *  is a deferral, not a failed pass. */
  displaced_by_seed?: number | null;
}

/** The ledger body the daemon sends. It answers 200 in every case, failures
 *  included, so nothing here means "an error happened" on its own:
 *  - `configured: false` is the only thing saying the daemon has no pr-reviewer.
 *  - `counts: null` says the daemon could not tell. Zeroes would instead claim
 *    the pr-reviewer looked and found none, which is a different fact.
 *  - `last_run: null` on an answer the pr-reviewer actually served says no pass
 *    has ever run, not that a pass came back clean. Read `configured` and the
 *    degraded markers first: every degraded body nulls `last_run` as well.
 *  - `total` and `has_more` are the only things separating a truncated page of
 *    findings from a complete one. `items.length` is never the whole story.
 *  `ok`, `branch` and `status` reach the client only through the spread of the
 *  pr-reviewer's own body, so they are absent from every degraded answer. */
export interface LedgerResponse {
  items: LedgerFinding[];
  counts: LedgerCounts | null;
  last_run: LedgerRun | null;
  /** The newest pass that finished. Absent on a Bob that predates the field. */
  last_completed_run?: LedgerRun | null;
  /** Whether any pass on this branch has finished. Absent on a Bob that predates the field. */
  reviewed?: boolean;
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  /** False when the daemon has no pr-reviewer URL/token configured. */
  configured: boolean;
  repo?: string;
  ok?: boolean;
  branch?: string;
  status?: string;
  /** Which degraded case this is. `pr_reviewer_bad_body` means the pr-reviewer
   *  answered 200 with something that is not an object, and so is reachable;
   *  `pr_reviewer_unreachable` means it never answered. Kept apart on purpose. */
  error?: "pr_reviewer_bad_body" | "pr_reviewer_unreachable";
  /** The pr-reviewer's status code, when it answered something other than 200. */
  upstream?: number;
  /** "no_github_repo" when the project's repo url is not one the daemon can
   *  read, so the request never reached the pr-reviewer at all. */
  note?: string;
}

/** Fetch the current code-review queue for a project. The daemon resolves the
 *  repo, injects the pr-reviewer token, and degrades softly (200 + empty list) on
 *  any upstream problem, so this only throws on a genuine dashboard-auth failure. */
export async function listCodeReviewQueue(project: string): Promise<CodeReviewQueueResponse> {
  const res = await fetch(`${DAEMON_URL}/projects/${encodeURIComponent(project)}/code-review/queue`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  return (await res.json()) as CodeReviewQueueResponse;
}

/** Fetch the pr-reviewer's ledger of findings for a project. The daemon resolves
 *  the repo, injects the pr-reviewer token, and answers 200 with an empty ledger
 *  on every upstream problem, so a degraded body resolves here rather than
 *  throwing and the tab can show a calm "unavailable" state instead of an error
 *  banner. Only throws on a dashboard auth or project-access failure.
 *
 *  `status` filters to open, fixed or dismissed. Omit it to let the pr-reviewer
 *  apply its own default; the daemon drops any other value rather than
 *  forwarding it, so a typo cannot turn into an upstream 400.
 *
 *  `page` asks for a slice: `limit` rows starting at `offset`. The answer's
 *  `has_more` and `total` say whether there is another slice to ask for. */
export async function listLedger(
  project: string,
  status?: string,
  page?: { limit?: number; offset?: number },
): Promise<LedgerResponse> {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  // The daemon refuses a malformed value with 400 and clamps a limit above Bob's ceiling.
  if (page?.limit !== undefined) qs.set("limit", String(page.limit));
  if (page?.offset !== undefined) qs.set("offset", String(page.offset));
  const query = qs.toString();
  const url = `${DAEMON_URL}/projects/${encodeURIComponent(project)}/code-review/ledger${query ? `?${query}` : ""}`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  return (await res.json()) as LedgerResponse;
}

/** Admin queues a manual PR review. The daemon resolves the repo, injects the
 *  pr-reviewer token, and is admin-gated. Resolves with the upstream result so the
 *  caller can surface `error` inline; on success the caller should refresh the
 *  panel to pick up the new queue item. Only throws on a dashboard-auth failure. */
export async function addManualReview(
  project: string,
  prNumber: number,
): Promise<{ ok: boolean; item?: CodeReviewItem; error?: string; configured?: boolean }> {
  const res = await fetch(`${DAEMON_URL}/projects/${encodeURIComponent(project)}/code-review/manual`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ prNumber }),
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  return (await res.json()) as { ok: boolean; item?: CodeReviewItem; error?: string; configured?: boolean };
}

/** Admin removes a queued review (most useful to drop an automatic entry). The
 *  daemon is admin-gated and degrades softly. Throws on a dashboard-auth failure
 *  or an upstream-reported failure so the caller can surface it inline; on success
 *  the caller should refresh the panel. */
/** Both writes forward Bob's status, so unlike `listLedger` they throw on a
 *  refusal. `already_dismissed` and `already_open` mean somebody got there first. */
async function ledgerAction(project: string, id: number, action: "dismiss" | "reopen", body?: unknown): Promise<LedgerFinding> {
  const res = await fetch(
    `${DAEMON_URL}/projects/${encodeURIComponent(project)}/code-review/ledger/${id}/${action}`,
    {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    },
  );
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string; configured?: boolean };
      if (j?.error) msg = j.error;
      else if (j?.configured === false) msg = "Code review is not connected";
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  const j = (await res.json()) as { ok?: boolean; item?: LedgerFinding; error?: string };
  if (!j.ok || !j.item) throw new Error(j.error ?? "could not update the finding");
  return j.item;
}

/** Permanent. Bob absorbs later sightings of this defect through its fingerprint. */
export async function dismissFinding(project: string, id: number, reason: string): Promise<LedgerFinding> {
  return ledgerAction(project, id, "dismiss", { reason });
}

export async function reopenFinding(project: string, id: number): Promise<LedgerFinding> {
  return ledgerAction(project, id, "reopen");
}

export async function removeReview(project: string, id: number): Promise<void> {
  const res = await fetch(`${DAEMON_URL}/projects/${encodeURIComponent(project)}/code-review/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  const j = (await res.json()) as { ok?: boolean; error?: string; configured?: boolean };
  if (!j.ok) throw new Error(j.error ?? (j.configured === false ? "Code review not connected" : "could not remove"));
}
